import type { QueryClient } from '@tanstack/react-query';
import { fetchEntityProductEntitlements } from '../services/entityApi';
import type { EntityApplication, Logger } from '../types';

export function entityProductEntitlementsQueryKey(entityUuid: string) {
  return ['entityProductEntitlements', entityUuid] as const;
}

// Recheck periodically so a transient failure isn't stuck at the fail-closed
// `[]` value for the rest of the session — only an entity switch or logout
// would otherwise create a new query key and retry.
const RECOVERY_POLL_INTERVAL_MS = 5 * 60 * 1000;

export function entityProductEntitlementsQueryOptions({
  entityUuid,
  getAccessTokenSilently,
  logger,
  queryClient,
}: {
  entityUuid: string;
  getAccessTokenSilently: () => Promise<string>;
  logger: Logger;
  queryClient: QueryClient;
}) {
  const queryKey = entityProductEntitlementsQueryKey(entityUuid);
  const fetchOnce = async () => {
    const token = await getAccessTokenSilently();
    return fetchEntityProductEntitlements(entityUuid, token, logger);
  };

  return {
    queryKey,
    queryFn: async (): Promise<EntityApplication[]> => {
      // Fail closed, not crashed: this signal gates one product tab, not the
      // whole PSP shell. Any transport/auth failure (token refresh, 403, 404,
      // 5xx) resolves to `[]` instead of rethrowing into EntityProvider's
      // single QueryBoundary and taking down every product. Retry once
      // in-process — react-query's own `retry` option never fires here
      // because this catch always resolves the query successfully.
      try {
        return await fetchOnce();
      } catch {
        try {
          return await fetchOnce();
        } catch (error) {
          // A refetch failure must not overwrite a known-good answer: rethrow so
          // react-query keeps the previous data (and retries on the next poll).
          // Only the very first fetch fails closed to [].
          const previous = queryClient.getQueryData(queryKey);
          if (previous !== undefined) throw error;
          logger.warn('Failed to fetch entity product entitlements; failing closed to []', {
            entity_uuid: entityUuid,
            error_message: error instanceof Error ? error.message : String(error),
          });
          return [];
        }
      }
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: RECOVERY_POLL_INTERVAL_MS,
    retry: false,
  };
}
